/**
 * 通知センター — the data layer the screen talks to.
 *
 * ONE SWITCH PER TAB. Everything above this file (hooks, template, table) is written against the real
 * signatures, so bringing a tab live is: add it to REAL_TABS and fill in its request below. No component
 * changes. レセプト同期 is live; the other two still read mockApi.ts, which is deleted when the last of
 * them lands.
 */
import { apiClient } from '@/shared/lib/axios';
import { toNotificationItem, type RawNotification } from './adapter';
import {
  mockFetchList,
  mockMarkRead,
  mockRetry,
} from './mockApi';
import {
  NOTIFICATION_TAB,
  type NotificationBulkResult,
  type NotificationListQuery,
  DEFAULT_PER_PAGE,
  type NotificationListResult,
  type NotificationTab,
  type NotificationUnreadCounts,
} from '@/types/admin-notification-center';

/**
 * WHICH TABS ARE LIVE — per tab, not one switch for the screen.
 *
 * レセプト同期 is served by the real API (GET /admin/{site}/notification-center); the other two still run on
 * local sample data. Owner, 2026-08-30: the scope of this work is the rezept flow from 「レセプト発行」,
 * and nothing else.
 *
 * THIS USED TO BE ONE BOOLEAN FOR ALL THREE TABS, and flipping it would have emptied the others: a tab
 * with no grain to group by returns nothing rather than something wrong. A per-tab set is the only shape
 * that lets one tab go live without making the screen look broken.
 *
 * マスタ同期 went live 2026-09-02: 治療院 and 保険者 dispatch through the same pipeline as a rezept, so a
 * 「send」 there is one record and the three states derive from sync_states exactly as they do on the
 * rezept tab (SyncNotificationRepository::CATEGORY_MASTER_RUN).
 *
 * 審査システムからの通知 went live 2026-09-02 as well: 審査 sends six messages and RAH writes a row for the
 * FOUR that take money or a verdict back (返戻 / 一部不支給 / 返戻取消 / 消込解除). 審査済 and 入金済 are
 * applied but never announced — they fire for every rezept that completes normally and would bury the four.
 * That exclusion is now made where the row is written, NOT on the way out: filtering after paging left
 * `meta.total` counting rows the screen then dropped, so a page came back short.
 *
 * ⚠️ 住所 is NOT among them. The address masters travel as a whole-table FILE and write no sync_states row,
 * so a run for one would sit at 送信中 for ever — it needs its own hook before it can be listed.
 */
const REAL_TABS = new Set<NotificationTab>([
  NOTIFICATION_TAB.REZEPT,
  NOTIFICATION_TAB.MASTER,
  NOTIFICATION_TAB.INBOUND,
]);

export const isLiveTab = (tab: NotificationTab): boolean => REAL_TABS.has(tab);

/**
 * meta — snake_case on the wire, camelCase in the app. THE PAGER DEPENDS ON THIS.
 *
 * 🔴 THE BUG THIS FIXES (2026-09-02). The live branch used to pass `payload.meta` straight through, so
 * `meta.currentPage` was ALWAYS undefined (Laravel sends `current_page`) and the template's
 * `current={meta?.currentPage ?? 1}` fell back to 1 on every page. antd's Pagination does not fire
 * onChange for a click on the page it believes is current — so after moving to page 2 (which worked, the
 * URL and the rows both changed) the highlight stayed on 「1」, and clicking 「1」 was a no-op. The screen
 * was stuck on page 2 with no way back.
 *
 * `total` is the one key that happens to match, which is why the pager looked half-alive: the right number
 * of pages, always the wrong one highlighted. `per_page` still has to be mapped even though the 表示件数
 * picker it also broke is gone: the pager divides `total` by it to decide how many pages exist.
 *
 * The sample-data branch already returns camelCase, which is why マスタ同期 never showed the bug — and why
 * a fix belongs HERE, at the one place the two branches meet, rather than in the template.
 */
const toMeta = (raw: Record<string, unknown> | undefined | null): NotificationListResult['meta'] => {
  const pick = (...names: string[]): number => {
    for (const name of names) {
      const value = raw?.[name];
      if (typeof value === 'number') return value;
      if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
      }
    }

    return 0;
  };

  // Both spellings accepted: the server speaks snake_case today, and a later camelCase resource must not
  // silently reintroduce the same dead pager.
  const perPage = pick('perPage', 'per_page') || DEFAULT_PER_PAGE;
  const total = pick('total');

  return {
    currentPage: pick('currentPage', 'current_page') || 1,
    perPage,
    total,
    // Derived when absent rather than defaulted to 1: a lastPage of 1 with a total of 25 hides the pager
    // outright (AppPagination sets hideOnSinglePage).
    lastPage: pick('lastPage', 'last_page') || Math.max(1, Math.ceil(total / perPage)),
  };
};

/** Flat query string. Arrays go as repeated keys, which is what Laravel's request()->array() expects. */
const toParams = (q: NotificationListQuery) => ({
  tab: q.tab,
  page: q.page,
  per_page: q.perPage,
  from: q.from || undefined,
  to: q.to || undefined,
  read: q.read === 'all' ? undefined : q.read,
  // Repeated key rather than a comma list: 種別 values are a closed set today, but a comma list is the kind
  // of encoding that breaks silently the first time a value contains one.
  levels: q.levels.length ? q.levels : undefined,
  clinic_number: q.clinicNumber.trim() || undefined,
  clinic_name: q.clinicName.trim() || undefined,
  // Still an ARRAY on the wire even though the control now yields one value: the server reads
  // service_types as an array and intersects it against ['A2','R2'], so a scalar would arrive as an
  // empty intersection and silently return nothing.
  service_types: q.serviceType ? [q.serviceType] : undefined,
  patient: q.patient.trim() || undefined,
  // 種別 on 審査システムからの通知. Empty array omitted, so 「no selection」 stays 「all」 on the wire too.
  kinds: q.kinds.length ? q.kinds : undefined,
});

export const fetchNotificationList = async (
  siteType: string,
  query: NotificationListQuery
): Promise<NotificationListResult> => {
  if (!isLiveTab(query.tab)) return mockFetchList(query);

  const response = await apiClient.get<{
    data: { data: RawNotification[]; meta: Record<string, unknown>; summary: NotificationListResult['summary'] };
  }>(`/admin/${siteType}/notification-center`, { params: toParams(query) });

  const payload = response.data.data;
  const items = payload.data.map(toNotificationItem);

  // The 審査済 / 入金済 exclusion now happens WHERE THE ROW IS WRITTEN (2026-09-02), so nothing has to be
  // dropped here. The client-side net that used to sit at this spot could not work: filtering AFTER paging
  // left `meta.total` counting rows the screen then removed, so a page came back short — the comment that
  // stood here said as much and asked for exactly this fix.
  const visible = items;

  return {
    data: visible,
    meta: toMeta(payload.meta),
    summary: payload.summary,
  };
};

/**
 * Tab badges. MIXED, because the tabs are: the live one is counted by the server, the other two by the mock.
 *
 * The endpoint returns ONLY the keys it can answer for — a tab it does not serve is absent rather than 0.
 * That distinction is the whole reason this merges instead of replacing: a confident 「0」 on a tab with a
 * backlog is worse than no badge, because nobody re-checks a badge that says zero.
 */
export const fetchNotificationUnreadCounts = async (
  siteType: string
): Promise<NotificationUnreadCounts> => {
  const empty = { master: 0, rezept: 0, inbound: 0 } as NotificationUnreadCounts;

  try {
    const response = await apiClient.get<{ data: Partial<NotificationUnreadCounts> }>(
      `/admin/${siteType}/notification-center/unread-count`
    );

    return { ...empty, ...response.data.data };
  } catch {
    // A badge is not worth a broken screen, and it is not worth a WRONG number either. This used to fall
    // back to mockFetchUnreadCounts, which was harmless while a tab was mocked and became a liability the
    // day the header bell started reading the same counts: a failed poll would have put sample numbers on
    // the bell, and nobody re-checks a badge they have caught lying. Zeros, and the list tells the truth
    // when the tab is opened.
    return empty;
  }
};

/**
 * Both bulk actions take an ID LIST, not a filter.
 *
 * That is a deliberate constraint on the future API — and it is also the answer to 「like Gmail, current
 * page only」 (owner's manager, 2026-08-27): the screen can only send ids it has on screen, so a press
 * can never reach a row the operator has not seen. There is no 「apply to all N results」 path to build.
 *
 * 削除 was REMOVED on the same date. It only ever hid a row; the failed record stayed failed, so the button
 * offered tidiness that looked like resolution.
 */
export const markNotificationsRead = async (
  siteType: string,
  ids: number[],
  tab: NotificationTab = NOTIFICATION_TAB.REZEPT
): Promise<NotificationBulkResult> => {
  if (!isLiveTab(tab)) return mockMarkRead(ids);

  const response = await apiClient.post<{ data: NotificationBulkResult }>(
    `/admin/${siteType}/notification-center/mark-read`,
    { ids }
  );
  return response.data.data;
};

export const retryNotifications = async (
  siteType: string,
  ids: number[],
  tab: NotificationTab = NOTIFICATION_TAB.REZEPT
): Promise<NotificationBulkResult> => {
  if (!isLiveTab(tab)) return mockRetry(ids);

  const response = await apiClient.post<{ data: NotificationBulkResult }>(
    `/admin/${siteType}/notification-center/retry`,
    { ids }
  );
  return response.data.data;
};

export { downloadDriftCsv, downloadErrorCsv, downloadRunErrorCsv } from './errorCsv';
export { MOCK_CLINIC_OPTIONS } from './mockData';
